import * as ko from "knockout";


/**
 * Implement knockout binding "pliAnimatedPage".
 *
 * The binding must be connected to an expression that returns
 * 1-based indexes of pages to make visible (by using sliding animation).
 * Value of 0 ("None") will hide all pages. When a positive
 * page ID is specified, animation will be "forward" (new page slides
 * in from the right edge). When a negative page ID is specified, animation
 * will be "back" (new page slides in from the left edge).
 */
class AnimatedPageBindingHandler implements ko.BindingHandler {

    /**
     * Finds element to animate and pages to be animated (as jQuery objects).
     */
    init(element: any, valueAccessor: () => any, allBindingsAccessor?: ko.AllBindingsAccessor, viewModel?: any, bindingContext?: ko.BindingContext<any>): void {
        this._$animation = $(element);
        this._$pages = this._$animation.find(".pliAnimatedPage");
    }

    /**
     * Changes "_currentPageId" to bound value.
     * Triggers page animation to make the specified page visible.
     *
     * Page IDs are expected to be 1-based indexes. When a positive
     * page ID is specified, animation will be "forward" (new page slides
     * in from the right edge). When a negative page ID is specified, animation
     * will be "back" (new page slides in from the left edge).
     */
    update(element: any, valueAccessor: () => any, allBindingsAccessor?: ko.AllBindingsAccessor, viewModel?: any, bindingContext?: ko.BindingContext<any>): void {
        // validate new ID
        const newPageId: number = ko.unwrap(valueAccessor());
        if (typeof newPageId !== "number") { throw new Error("\"pliAnimatedPage\" requires observable that resolves to a number"); }
        const newPageIdAbs = (newPageId === null ? null : Math.abs(newPageId));
        const oldPageId = this._currentPageId;
        this._currentPageId = newPageIdAbs;

        // if switching from/to "null", only change visibility
        if (oldPageId === 0) {
            if (newPageIdAbs !== 0) {
                this._$pages.eq(newPageIdAbs - 1).addClass("active");
            }
            return;
        }
        if (newPageIdAbs === 0) {
            if (oldPageId !== 0) {
                this._$pages.eq(oldPageId - 1).removeClass("active");
            }
            return;
        }

        // if animation is required, queue it now
        if (oldPageId !== newPageIdAbs) {
            const forward = (newPageId === newPageIdAbs);
            this._$animation.velocity({
                properties: {
                    translateX: forward
                        ? AnimatedPageBindingHandler._forwardTranslation
                        : AnimatedPageBindingHandler._backTranslation
                },
                options: {
                    duration: 500,
                    easing: "easeOutCubic",
                    begin: (): void => this._startAnimation(oldPageId, newPageIdAbs, forward),
                    complete: (): void => this._completeAnimation(oldPageId, newPageIdAbs, forward)
                }
            });
        }
    }


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /**
     * Restore classes after animation has completed (only keep active page visible).
     */
    private _completeAnimation(from: number, to: number, forward: boolean): void {
        this._$animation.removeAttr("style");
        if (forward) {
            this._$pages.eq(from - 1).removeClass("active");
            this._$pages.eq(to - 1).removeClass("next").addClass("active");
        } else {
            this._$pages.eq(from - 1).removeClass("next");
        }
    }

    /**
     * Set classes before animation begins (make both from and to pages visible).
     */
    private _startAnimation(from: number, to: number, forward: boolean): void {
        if (forward) {
            this._$pages.eq(to - 1).addClass("next");
        } else {
            $.Velocity.hook(this._$animation, "translateX", "-50%"); // improves back animation in IE
            this._$pages.eq(from - 1).removeClass("active").addClass("next");
            this._$pages.eq(to - 1).addClass("active");
        }
    }

    /** Element containing "pliAnimatedPage" binding, used for Velocity animation */
    private _$animation: JQuery; // readonly, initialized in "init"

    /** Array of animation pages. Page IDs must be offset by -1 when referencing this array (page-ids are 1-based, while jQuery array is zero-based). */
    private _$pages: JQuery; // readonly, initialized in "init"

    /** Index of page that is currently visible to the user (or will become visible in the near future once animation queue has been cleared). */
    private _currentPageId: number = 0; // changed in "update"

    /** Velocity spec to use when moving pages to the right (new page emerging from left). */
    private static _backTranslation: string[] = ["0%", "-50%"];

    /** Velocity spec to use when moving pages to the left (new page emerging from right). */
    private static _forwardTranslation: string[] = ["-50%", "0%"];

}


/**
 * Register "pliAnimatedPage" as a Knockout binding.
 * After calling this function elements can use "pliAnimatedPage", e.g.:
 * <div data-bind="pliAnimatedPage: pages.current">...</div>.
 *
 * This function can be called multiple times, handler is only
 * registered on the first call (returns "true"), nothing
 * is done afterwards (returns "false").
 */
export function registerAnimatedPageBindingHandler(): boolean {
    // check if already registered
    if (ko.bindingHandlers["pliAnimatedPage"]) { return false; }

    // create handler + bind its methods (because Knockout doesn't preserve "this" context)
    const handler = new AnimatedPageBindingHandler();
    handler.init = handler.init.bind(handler);
    handler.update = handler.update.bind(handler);
    ko.bindingHandlers["pliAnimatedPage"] = handler;
    return true;
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/forms-signin/animatedPage.ts